<div class="flex flex-col gap-4">

    <header class="mb-1">
        <h2 class="text-xl font-semibold text-p-text m-0">
            {{ 'bookingV2.rechnungsversand.title' | translate }}
        </h2>
        <p class="text-sm text-p-text-muted m-0 mt-1">
            {{ 'bookingV2.rechnungsversand.subtitle' | translate }}
        </p>
    </header>

    @if (showSavedBookingDocuments()) {
        <section class="flex flex-col gap-2">
            <h3 class="text-sm font-semibold text-p-text m-0">
                {{ 'bookingV2.rechnungsversand.existingDocuments.title' | translate }}
            </h3>
            @if (savedDocumentsLoading()) {
                <p class="text-sm text-p-text-muted m-0">{{ 'common.loading' | translate }}</p>
            } @else if (savedDocumentsLoadFailed()) {
                <div class="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900">
                    <span class="material-icons text-base" aria-hidden="true">warning</span>
                    {{ 'bookingV2.rechnungsversand.existingDocuments.loadError' | translate }}
                </div>
            } @else if (savedDocuments().length === 0) {
                <p class="text-sm text-p-text-muted m-0 px-3 py-2 rounded-lg bg-p-surface-100 border border-p-border">
                    {{ 'bookingV2.rechnungsversand.existingDocuments.empty' | translate }}
                </p>
            } @else {
                <div class="border border-p-border rounded-lg overflow-hidden">
                    <div class="grid grid-cols-[1fr_6rem_5.5rem_auto] gap-2 px-3 py-2 bg-p-surface-100 text-xs font-medium text-p-text-muted border-b border-p-border">
                        <div>{{ 'bookingV2.rechnungsversand.existingDocuments.columns.type' | translate }}</div>
                        <div>{{ 'bookingV2.rechnungsversand.existingDocuments.columns.number' | translate }}</div>
                        <div class="text-right">{{ 'bookingV2.rechnungsversand.existingDocuments.columns.amount' | translate }}</div>
                        <div></div>
                    </div>
                    @for (record of savedDocuments(); track record.number) {
                        <div class="grid grid-cols-[1fr_6rem_5.5rem_auto] gap-2 px-3 py-2 items-center border-b border-p-border last:border-b-0 text-sm">
                            <div>{{ financialRecordTypeLabel(record) }}</div>
                            <div class="font-medium">{{ record.number }}</div>
                            <div class="text-right tabular-nums">{{ fmt(record.total) }}</div>
                            <div>
                                @if (canOpenSavedDocument(record)) {
                                    <p-button type="button" severity="secondary" size="small" [text]="true"
                                        (onClick)="openSavedDocument(record)">
                                        <span pButtonLabel>{{ 'accounting.financialRecords.actions.showVoucher' | translate }}</span>
                                    </p-button>
                                }
                            </div>
                        </div>
                    }
                </div>
            }
        </section>
    }

    <!-- A berth's Produkte could not be loaded: they are missing from the list below
         and would save silently, so say so instead of showing a plausible-looking
         but incomplete total. -->
    @if (berthProductsLoadFailed()) {
        <div class="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900">
            <span class="material-icons text-base" aria-hidden="true">warning</span>
            {{ 'bookingV2.rechnungsversand.berthProductsLoadError' | translate }}
        </div>
    }

    @if (rows().length === 0) {
        <div class="flex flex-col items-center justify-center gap-2 px-4 py-10 rounded-lg border border-dashed border-p-border text-center">
            <span class="material-icons text-p-text-muted" style="font-size: 2rem" aria-hidden="true">receipt_long</span>
            <p class="text-sm text-p-text-muted m-0">{{ 'bookingV2.rechnungsversand.empty' | translate }}</p>
        </div>
    } @else {

        <!-- Aggregated positions (Beziehung + erweiterte Leistungen) -->
        <div class="border border-p-border rounded-lg overflow-hidden">
            <div class="grid bg-p-surface-50 border-b border-p-border text-xs font-medium text-p-text-muted uppercase tracking-wide"
                 style="grid-template-columns: auto 1fr auto auto auto;">
                <div class="px-3 py-2 flex items-center">
                    <p-checkbox [binary]="true" [ngModel]="allSelected()" [indeterminate]="someSelected()"
                        (onChange)="toggleSelectAll()"
                        [ariaLabel]="('bookingV2.rechnungsversand.selectAll' | translate)" />
                </div>
                <div class="px-3 py-2">{{ 'bookingV2.rechnungsversand.columns.name' | translate }}</div>
                <div class="px-3 py-2 text-right">{{ 'bookingV2.rechnungsversand.columns.quantity' | translate }}</div>
                <div class="px-3 py-2">{{ 'bookingV2.rechnungsversand.columns.unit' | translate }}</div>
                <div class="px-3 py-2 text-right">{{ 'bookingV2.rechnungsversand.columns.gross' | translate }}</div>
            </div>

            <div class="divide-y divide-p-border">
                @for (row of rows(); track row.key) {
                    <div class="grid items-start bg-p-surface-50" style="grid-template-columns: auto 1fr auto auto auto;">
                        <div class="px-3 py-2 flex items-center">
                            <p-checkbox [binary]="true" [ngModel]="isSelected(row.key)"
                                [disabled]="!row.billable"
                                (onChange)="toggleSelected(row)"
                                [ariaLabel]="('bookingV2.rechnungsversand.selectRowAria' | translate)" />
                        </div>
                        <div class="px-3 py-2 min-w-0">
                            <div class="flex items-center gap-1.5 flex-wrap">
                                <span class="font-medium text-p-text text-sm truncate">{{ row.name }}</span>
                                @if (row.hasDynamicPrice) {
                                    <span class="px-1.5 py-0.5 bg-amber-100 text-amber-800 text-xs rounded-full inline-flex items-center gap-0.5">
                                        <span class="material-icons text-[10px] leading-none" aria-hidden="true">auto_awesome</span>
                                        {{ 'bookingV2.dynamicPriceBreakdown.badge' | translate }}
                                    </span>
                                }
                                @switch (row.source) {
                                    @case ('relation') {
                                        <span class="px-1.5 py-0.5 bg-orange-100 text-orange-800 text-xs rounded-full inline-flex items-center gap-0.5">
                                            <span class="material-icons text-[10px] leading-none" aria-hidden="true">sell</span>
                                            {{ rechnungsversandRelationKey() | translate }}
                                        </span>
                                    }
                                    @case ('subscription') {
                                        <span class="px-1.5 py-0.5 bg-violet-100 text-violet-800 text-xs rounded-full inline-flex items-center gap-0.5">
                                            <span class="material-icons text-[10px] leading-none" aria-hidden="true">autorenew</span>
                                            {{ 'bookingV2.rechnungsversand.source.subscription' | translate }}
                                        </span>
                                    }
                                    @case ('electric') {
                                        <span class="px-1.5 py-0.5 bg-amber-100 text-amber-800 text-xs rounded-full inline-flex items-center gap-0.5">
                                            <span class="material-icons text-[10px] leading-none" aria-hidden="true">bolt</span>
                                            {{ 'bookingV2.rechnungsversand.source.electric' | translate }}
                                        </span>
                                    }
                                    @case ('berth') {
                                        <span class="px-1.5 py-0.5 bg-emerald-100 text-emerald-800 text-xs rounded-full inline-flex items-center gap-0.5">
                                            <span class="material-icons text-[10px] leading-none" aria-hidden="true">anchor</span>
                                            {{ 'bookingV2.rechnungsversand.source.berth' | translate }}
                                        </span>
                                    }
                                    @case ('deposit') {
                                        <span class="px-1.5 py-0.5 bg-slate-100 text-slate-700 text-xs rounded-full inline-flex items-center gap-0.5">
                                            <span class="material-icons text-[10px] leading-none" aria-hidden="true">savings</span>
                                            {{ 'bookingV2.rechnungsversand.source.deposit' | translate }}
                                        </span>
                                    }
                                    @default {
                                        <span class="px-1.5 py-0.5 bg-sky-100 text-sky-800 text-xs rounded-full inline-flex items-center gap-0.5">
                                            <span class="material-icons text-[10px] leading-none" aria-hidden="true">tune</span>
                                            {{ 'bookingV2.rechnungsversand.source.service' | translate }}
                                        </span>
                                    }
                                }
                            </div>
                            <!-- Dynamic price composition: subtle, inline in the name column
                                 beneath the badges (no expand needed). -->
                            @if (row.hasDynamicPrice && row.dynamicPrice) {
                                <booking-v2-dynamic-price-breakdown class="block mt-1.5" [dynamicPrice]="row.dynamicPrice" />
                            }
                        </div>
                        <div class="px-3 py-2 text-right text-sm text-p-text whitespace-nowrap">{{ fmtQty(row.quantity) }}</div>
                        <div class="px-3 py-2 text-sm text-p-text truncate">@if (row.unitLabelKey) { {{ row.unitLabelKey | unitTypesField }} } @else { – }</div>
                        <div class="px-3 py-2 text-right text-sm font-semibold text-p-text whitespace-nowrap">{{ fmt(row.gross) }} €</div>
                    </div>
                }
            </div>
        </div>

        @if (hasRelationRows()) {
            <p class="text-xs text-p-text-muted m-0 -mt-1">{{ 'bookingV2.rechnungsversand.priceHint' | translate }}</p>
        }

        <!-- A berth product configured with quantity 0 is a placeholder: it is listed
             so the user sees it, but stays unbillable until a quantity is entered in
             the Leistungen tab. -->
        @if (hasUnbillableBerthRow()) {
            <div class="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-p-surface-100 border border-p-border">
                <span class="flex items-center gap-1.5 text-sm text-p-text-muted">
                    <span class="material-icons text-base" aria-hidden="true">info</span>
                    {{ 'bookingV2.rechnungsversand.zeroQuantityHint' | translate }}
                </span>
                <p-button type="button" size="small" [text]="true" (onClick)="goToStep('advanced')">
                    <span pButtonLabel>{{ 'bookingV2.rechnungsversand.editServices' | translate }}</span>
                    <span pButtonIcon class="material-icons" style="font-size: 1rem">arrow_forward</span>
                </p-button>
            </div>
        }

        <!-- Dispatch action: choose Rechnung/Quittung, take selected positions over -->
        <div class="flex flex-col gap-3 px-3 py-3 rounded-lg bg-p-surface-100 border border-p-border">
            <div class="flex items-center justify-between gap-3 flex-wrap">
                <span class="text-sm font-medium text-p-text">
                    {{ 'bookingV2.rechnungsversand.selectedSummary' | translate: { count: selectedCount(), gross: fmt(selectedGross()) } }}
                </span>
                <u2b-segmented-toggle
                    [options]="kindOptions"
                    [value]="belegKind()"
                    (valueChange)="setKind($event)"
                    [ariaLabel]="('bookingV2.rechnungsversand.kindAria' | translate)" />
            </div>

            <div class="flex items-center gap-3 flex-wrap">
                <p-button type="button" severity="primary" size="small"
                    [disabled]="selectedCount() === 0"
                    (onClick)="armBeleg()">
                    <span pButtonIcon class="material-icons" style="font-size: 1.125rem">playlist_add_check</span>
                    <span pButtonLabel>{{ 'bookingV2.rechnungsversand.takeOver' | translate }}</span>
                </p-button>

                @if (armed()) {
                    <span class="inline-flex items-center gap-1.5 text-sm text-emerald-700">
                        <span class="material-icons text-base" aria-hidden="true">check_circle</span>
                        {{ 'bookingV2.rechnungsversand.armedHint' | translate }}
                        <button type="button" class="ml-1 underline bg-transparent border-0 p-0 text-p-text-muted cursor-pointer" (click)="disarm()">
                            {{ 'bookingV2.rechnungsversand.undo' | translate }}
                        </button>
                    </span>
                }
            </div>
        </div>
    }
</div>
